import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LandingModelService } from './landingModel.service';
import { GtmService } from './gtm.service';
import { OffersService } from './offers.service';
import { Subject } from 'rxjs';
import { MetadataService } from './metadata.service';

declare var window: any;

interface QueuedTrackingData {
  topicName: string;
  data: any;
}

export enum UpaTarget {
  CARD = 'card',
  BUTTON = 'button',
  OFFER = 'offer',
  OFFER_TITLE = 'offer-title',
  OFFER_COMPLETE_SBOX = 'offer-complete-sbox',
  SWITCH = 'switch',
  FILTER = 'filter',
  IMAGE = 'image',
  VIDEO = 'video',
  BANNER = 'banner',
  SEARCHBOX = 'searchbox',
  HEADING = 'heading',
  PREHEADING = 'preheading',
  USER_ACTIVITY = 'userActivity',
  MAP = 'map',
  SLIDER = 'slider',
  TAB = 'tab',
  WIZUMEN_MODAL = 'wizumen-modal',
  WIZUMEN = 'wizumen',
  WIZUMEN_TRIP_PLANNER = 'wizumen-trip-planner',
  WIZUMEN_ITERATIONS_V2 = 'wizumen-iterations-v2',
  CHAT_AI = 'chat-ai',
  REEL = 'reel',
  SWIPE_TO_BUY_MODAL = "swipe-to-buy-modal",
  SWIPE_TO_BUY_MODAL_FROM_CTA = "swipe-to-buy-modal-from-cta",
  TRIP_PLANNER = 'trip-planner',
  TIMMINGS = 'timmings',
  FAVORITES = 'favorites',
  EVENT_DISPATCHER = 'event-dispatcher',
  ALERT = 'alert',
  TAG = 'tag',
  ACCORDION = 'accordion',
  EMPTY_STATE = 'empty-state',
  SUBNAV = 'subnav'
}

type UpaTargetString = `${UpaTarget}`;

export interface UpaTrackingData {
  moduleId: string;
  moduleIndex: number;
  uniqueId?: string;
  target: UpaTarget;
  label?: string;
  positionIndex?: number;
  href?: string;
  dc?: string;
  flags?: string[];
  data?: { [key: string]: string };
  unique_id?: string;
}

export interface UpaTrackingDataWithEventType extends UpaTrackingData {
  eventtype?: string;
}

export interface LandingTopicSchema {
  pageview_id: string;
  eventtype: string;
  lname?: string;
  ltype?: string;
  cc?: string;
  ch?: string;
  pr?: string;
  company_id?: string;
  uow?: string;
  userid?: string;
  url?: string;
  logged_level?: string;
  ab?: string;
  trip_status?: string;
  dc?: string;
  fl?: string;  
  modules?: ModuleTrackingData[];
}

interface ModuleTrackingData {
  module_id: string;
  unique_id: string;
  eventtype: string;
  label?: string;
  target?: UpaTargetString;
  target_url?: string;
  row_number?: number;
  position?: number;
  flags?: string[];
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class UpaService {
  public static readonly LANDING_TOPIC = 'landing';
  private viewsTracked: Set<string> = new Set<string>();
  private queue: QueuedTrackingData[] = [];
  private activeRequests = 0;
  private readonly MAX_CONCURRENT_REQUESTS = 30;
  private trackViewList: any[] = [];

  private trackingDataSubject = new Subject<UpaTrackingDataWithEventType>();
  trackingData$ = this.trackingDataSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: string,
    public landingModelService: LandingModelService,
    private gtmService: GtmService,
    private metadataService: MetadataService) { }

  // Data compartida por trackeo de pageview, show y click
  resolveCommonData() {
    if (isPlatformBrowser(this.platformId) && window.upaData) {
      const upaData = window.upaData;
      const landingModel = this.landingModelService.getLandingModel();

      return {
        ab: landingModel.abTests && landingModel.abTests.ab ? landingModel.abTests.ab : undefined,
        cc: upaData.cc,
        ch: upaData.ch,
        pr: upaData.pr,
        fl: upaData.fl,
        logged_level: upaData.loggedLevel,
        pageview_id: upaData.pageview_id,
        trip_status: upaData.trip_status,
        userid: upaData.userid,
        url: upaData.url,
        ltype: upaData.ltype,
        lname: upaData.lname,
        company_id: upaData.company_id,
        uow: upaData.xuowHeader
      };
    } else {
      return {};
    }
  }

  trackPageview() {
    const commonData = this.resolveCommonData();
    const specificData = {
      eventtype: 'pageview',
      module_id: 'LandingPageview'
    };

    const pageviewData = Object.assign(commonData, specificData);

    this.trackTopic(UpaService.LANDING_TOPIC, pageviewData);

  }

  trackAjaxSectionPageView() {
    this.trackPageview();
  }

  trackModulesView() {
    //
    const commonData = this.resolveCommonData();
    const modules = this.landingModelService.getLandingModel().modules
      .filter(module => module.id)
      .filter(module => !this.excludedFromAutomaticViewTrack(module));

    this.trackModulesList(modules, commonData);
  }

  private trackModulesList(
    modules: any[],
    commonData: any,
    parentIndex?: number
  ): ModuleTrackingData[] {
    const modulesTracking: ModuleTrackingData[] = [];

    modules.forEach((module, index) => {
      // Usar parentIndex si existe, sino usar index actual
      const actualIndex = parentIndex !== undefined ? parentIndex : index;
      const showData = {
        eventtype: 'view',
        module_id: module.id,
        unique_id: module.unique_id,
        row_number: actualIndex,
        // position // TODO: agregar para la v2
        label: module.id === 'DocumentModule' ? module?.data?.link : module?.title?.text,
        data: this.getModuleTrackingData(module),
        target_url: module?.data?.link
      };
      module.row_number = actualIndex;

      module.data?.upa_data && Object.assign(showData, module.data.upa_data);

      if (module.id === 'WizumenModule') {
        showData.label = "XSELL_WIZUMEN_NEW";
      }

      // los módulos de ofertas de landing de vuelos a destino pueden venir vacíos y no deben trackearse 
      // (vienen así para luego mantener la posición si se quita un pre-filtro como el de aerolíneas y pasa a tener resultados)
      if (module.id !== 'FlightOffersModule' || module.data.favorites_response.favorites.length > 0) {
        modulesTracking.push(showData);
      }

      // para el modulo de multibanner trackeo los views individuales de cada banner
      if (module.id == 'MultiBannerModule') {
        module.data?.cards?.forEach((banner: any, bannerIndex: any) => {
          const trackData = { ...showData, ...{ eventtype: 'view-banner', label: banner.id || 'image', target_url: banner?.link, position: bannerIndex } };
          modulesTracking.push(trackData);
        });
      }
      // para el modulo de picture-list trackeo los views individuales de cada imagen
      if (module.id == 'PictureListModule') {
        module.data?.pictures?.forEach((picture: any, index: number) => {
          const trackData = { ...showData, ...{ eventtype: 'view-picture', label: picture?.alt || 'image', target_url: picture?.link, position: index } };
          modulesTracking.push(trackData);
        });
      }
      // para el modulo de EvaCardsWrapper trackeo los views individuales de cada card
      if (module.id == 'EvaCardsWrapperModule') {
        module.data?.eva_cards?.forEach((card: any, cardIndex: any) => {
          const trackData = { ...showData, ...{ eventtype: 'view-card', label: card?.title?.text || 'card', target_url: card?.link, position: cardIndex } };
          modulesTracking.push(trackData);
        });
      }
      // para los módulos de ofertas trackeo los views individuales de cada oferta
      if (OffersService.useOfferModule(module.id)) {
        module.data?.forEach((offer: any, index: number) => {
          const trackData: ModuleTrackingData = {
            ...showData,
            ...{
              eventtype: 'view-offer',
              label: offer.recommendation_id || 'offer',
              target_url: offer?.link?.url,
              target: 'offer',
              unique_id: module.unique_id,
              flags: [...(commonData.flags || []), ...(offer.flags || [])],
              position: index
            }
          };
          modulesTracking.push(trackData);
        });
      }
      if (module.id === 'GroupedModules') {
        module.data?.tabs?.forEach((tab: any, tabIndex: any) => {
          const trackData = {
            ...showData,
            ...{
              eventtype: 'view-grouped-module-tab',
              label: tab.title_tab,
              position: tabIndex
            }
          };
          modulesTracking.push(trackData);
          if (tab.modules && tab.modules.length > 0) {
            // Acumular los módulos de las tabs en el mismo array
            const nestedModules = this.trackModulesList(tab.modules, commonData, actualIndex);
            modulesTracking.push(...nestedModules);
          }
        });
      }

      // Almacenar el trackeo de view ya que no usamos mas trackTopic()
      this.trackViewList.push(Object.assign({}, commonData, showData));
    });

    // Solo enviar si es la llamada principal (sin parentIndex)
    if (parentIndex === undefined && modulesTracking.length > 0) {
      this.sendTrackingPool(UpaService.LANDING_TOPIC, commonData, modulesTracking);
    }

    return modulesTracking;
  }

  resolveData(data: UpaTrackingDataWithEventType) {
    const commonData = this.resolveCommonData();
    const specificData = {
      eventtype: data.eventtype || 'click',
      module_id: data.moduleId,
      unique_id: data.uniqueId,
      label: data.label,
      target: data.target,
      row_number: data.moduleIndex,
      position: data.positionIndex,
      target_url: data.href,
      dc: data.dc,
      flags: data.flags,
      data: data.data
    };

    return Object.assign(commonData, specificData);
  }

  markAsTracked(trackingData: any): void {
    this.viewsTracked.add(this.getKey(trackingData));
  }

  hasTrackedView(trackingData: any): boolean {
    return this.viewsTracked.has(this.getKey(trackingData));
  }

  trackViewPort(unique_id: string, module_id: string) {
    const data = {
      eventtype: "viewport",
      unique_id: unique_id,
      module_id: module_id,
      pageview_id: window.upaData?.pageview_id
    };

    if (!this.hasTrackedView(data)) {
      this.trackTopic(UpaService.LANDING_TOPIC, data);
      this.addTrackingDataToMetadata(data);
      this.markAsTracked(data);
    }
  }

  trackView(upaTrackingData: UpaTrackingData, eventType: string = 'view') {
    const data: any = this.resolveData({
      ...upaTrackingData,
      eventtype: eventType
    });
    this.addTrackingDataToMetadata(data);

    const batchData: LandingTopicSchema = {
      ...data,
      eventtype: 'batch',
      modules: [{
        module_id: data.module_id,
        unique_id: data.unique_id,
        eventtype: eventType,
        label: data.label,
        target: data.target,
        target_url: data.href,
        row_number: data.row_number,
        position: data.position,
        flags: data.flags,
        data: data.data
      }]
    }

    this.trackTopic(UpaService.LANDING_TOPIC, data);
    this.trackTopic(UpaService.LANDING_TOPIC, batchData);
  }

  trackClickTopic(upaTrackingData: UpaTrackingData, event?: MouseEvent, trackGtm: boolean = true) {
    if (this.metadataService.isXDespShowInfoEnabled() && event) {
      const cancelPageLoad = !confirm("¿Ir al enlace?\n" + (upaTrackingData.href || ''));
      if (cancelPageLoad) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
    const data: any = this.resolveData({
      ...upaTrackingData,
      eventtype: 'click'
    });

    this.addTrackingDataToMetadata(data);

    this.trackTopic(UpaService.LANDING_TOPIC, data);
    if (trackGtm) {
      const { uniqueId, positionIndex, label } = upaTrackingData;
      const hasPositionIndex = positionIndex !== undefined && positionIndex !== null;
      const detailedLabel = (hasPositionIndex ? `position-${positionIndex}|` : "") + label;
      this.gtmService.trackEvent('user-action', uniqueId!, 'Click', detailedLabel);
    }
  }

  track(data: any) {
    if (window['UpaDataTracker']) {
      window.UpaDataTracker.send(Object.assign({}, window.upaData, data));
    }
  }

  private getDisableTracking(): boolean {
    return (this.landingModelService.getLandingModel().disableTracking || this.landingModelService.getLandingModel().isCachedVersion) ?? false;
  }

  trackTopic(topicName: string, data: any) {
    const disableTracking = this.getDisableTracking();
    if (isPlatformBrowser(this.platformId) && data != null && !disableTracking) {
      // Si el evento es 'user-leaves', usamos sendBeacon para evitar bloqueos en el beforeunload. 
      // En los otros casos, usamos fetch con una cola.
      if (data.eventtype === 'user-leaves') {
        this.queue.push({ topicName, data });
        this.flushQueueWithBeacon();
      } else {
        // Se almacenan los trackeos de view ya que se envían antes de renderizar info-metadata
        if (data.eventtype === 'view') {
          this.trackViewList.push(data);
        }
        this.enqueueTracking(topicName, data);
      }
    }
  }

  private flushQueueWithBeacon() {
    const headers: { [key: string]: string } = { 'Accept': 'application/json', 'type': 'application/json' };
    while (this.queue.length > 0) {
      const item = this.queue.shift();
      if (item) {
        const blob = new Blob([JSON.stringify(item.data)], headers);
        navigator.sendBeacon(`/hermes-service/topic/${item.topicName}`, blob);
      }
    }
    this.activeRequests = 0;
  }

  private enqueueTracking(topicName: string, data: any) {
    const queuedData: QueuedTrackingData = {
      topicName,
      data,
    };

    this.queue.push(queuedData);
    this.processQueue();
  }

  private async processQueue() {
    while (this.queue.length > 0 && this.activeRequests < this.MAX_CONCURRENT_REQUESTS) {
      const item = this.queue.shift();
      if (item) {
        this.activeRequests++;
        try {
          await this.sendTrackingData(item);
        } catch (error) {
          console.error('Error sending tracking data:', error);
        } finally {
          this.activeRequests--;
          if (this.queue.length > 0) {
            Promise.resolve().then(() => this.processQueue());
          }
        }
      }
    }
  }

  private async sendTrackingData(item: QueuedTrackingData): Promise<void> {
    try {
      const response = await fetch(`/hermes-service/topic/${item.topicName}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(item.data)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

    } catch (error) {
      console.warn('UPA tracking failed:', error);

    }
  }

  private sendTrackingPool(topicName: string, data: any, modules: ModuleTrackingData[]): void {
    const disableTracking = this.getDisableTracking();
    if (!isPlatformBrowser(this.platformId) || modules.length === 0 || disableTracking) {
      return;
    }
    const payload = { ...data, eventtype: 'batch', modules };
    this.enqueueTracking(topicName, payload);
    modules.forEach(module => {
      const moduleData = { ...data, ...module };
      this.trackTopic(topicName, moduleData);
    });
  }

  excludedFromAutomaticViewTrack(module: any) {
    return [
      'AssistanceModalModule',
      'SofiaPromptSuggestionsModule'
    ].includes(module.id)
      || module.track_when_displayed === true;
  }

  getModuleTrackingData(module: any) {
    return {
      recommendation_id: module.data?.[0]?.recommendation_id,
      product: module.data?.[0]?.product,
      offers: module.data?.length?.toString()
    };
  }

  addTrackingDataToMetadata(trackingData: any): void {
    this.trackingDataSubject.next(trackingData);
  }

  private getKey(data: any): string {
    return JSON.stringify({
      eventtype: data.eventtype,
      row_number: data.moduleIndex,
      unique_id: data.unique_id,
      module_id: data.module_id,
      position: data.positionIndex
    })
  }

  getViewsTracked() {
    return this.trackViewList;
  }

  getViewTrackData(uniqueId: string | undefined) {
    return this.trackViewList.find(view => view.unique_id === uniqueId);
  }
}
